import { type TimeSlot } from "@src/appV2/redesign/Shift/types";
import { addMinutes, differenceInMinutes } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- @clipboard-health/date-time doesn't export zonedTimeToUtc equivalent
import { formatInTimeZone, zonedTimeToUtc } from "date-fns-tz";
import { useCallback, useMemo, useState } from "react";

import { useDefaultShiftTimes } from "../../ShiftPostingForm/hooks/useDefaultShiftTimes";

interface UseEditableShiftFieldsOptions {
  workplaceId: string;
  timezone: string;
  initialQualification: string;
  initialDate: Date;
  initialTimeSlot: TimeSlot;
  initialStartTime: Date;
  initialEndTime: Date;
}

interface UseEditableShiftFieldsResult {
  qualification: string;
  date: Date;
  timeSlot: TimeSlot;
  startTime: Date;
  endTime: Date;
  setQualification: (qualification: string) => void;
  setDate: (date: Date) => void;
  setTimeSlot: (timeSlot: TimeSlot) => void;
  setStartTime: (time: Date) => void;
  setEndTime: (time: Date) => void;
  /** The workplace's static configured default times for the current (qualification, slot) selection. */
  defaultTimes: { start: Date; end: Date } | undefined;
  /** Whether the workplace has qualification-specific shift times enabled. */
  isQualificationSpecificEnabled: boolean;
}

/** Keeps `time`'s hour/minute (in `timezone`) but moves it onto `referenceDate`'s calendar day. */
function reanchorToDate(time: Date, referenceDate: Date, timezone: string): Date {
  const year = referenceDate.getFullYear();
  const month = String(referenceDate.getMonth() + 1).padStart(2, "0");
  const day = String(referenceDate.getDate()).padStart(2, "0");
  const timeOfDay = formatInTimeZone(time, timezone, "HH:mm");
  return zonedTimeToUtc(`${year}-${month}-${day}T${timeOfDay}:00`, timezone);
}

/**
 * Editable role/date/shift-type/time state for an invite whose shift already
 * exists (posting cards, Chat, My Professionals, Worker Profile, Turnover
 * Management) — the Add Shift panel's own field components are reused as-is,
 * seeded from the shift's current values instead of starting blank. Changing
 * role/date/shift-type falls back to the workplace's default time for the new
 * selection; until that resolves (or if it's unavailable), the shift's
 * original time-of-day is kept but re-anchored onto the selected date, since
 * `startTime`/`endTime` rather than `date` are what's sent to create the invite.
 */
export function useEditableShiftFields(
  options: UseEditableShiftFieldsOptions
): UseEditableShiftFieldsResult {
  const {
    workplaceId,
    timezone,
    initialQualification,
    initialDate,
    initialTimeSlot,
    initialStartTime,
    initialEndTime,
  } = options;

  const [qualification, setQualificationState] = useState(initialQualification);
  const [date, setDateState] = useState(initialDate);
  const [timeSlot, setTimeSlotState] = useState(initialTimeSlot);
  const [customStartTime, setCustomStartTime] = useState<Date | undefined>(initialStartTime);
  const [customEndTime, setCustomEndTime] = useState<Date | undefined>(initialEndTime);

  const { defaultTimes, isQualificationSpecificEnabled } = useDefaultShiftTimes({
    workplaceId,
    qualification,
    timeSlot,
    selectedDate: date,
    timezone,
  });

  const initialDurationInMinutes = useMemo(
    () => differenceInMinutes(initialEndTime, initialStartTime),
    [initialStartTime, initialEndTime]
  );

  const noDefaultFallbackStartTime = useMemo(
    () => reanchorToDate(initialStartTime, date, timezone),
    [initialStartTime, date, timezone]
  );

  const noDefaultFallbackEndTime = useMemo(
    () => addMinutes(noDefaultFallbackStartTime, initialDurationInMinutes),
    [noDefaultFallbackStartTime, initialDurationInMinutes]
  );

  const clearCustomTimes = useCallback(() => {
    setCustomStartTime(undefined);
    setCustomEndTime(undefined);
  }, []);

  const setQualification = useCallback(
    (next: string) => {
      setQualificationState(next);
      clearCustomTimes();
    },
    [clearCustomTimes]
  );

  const setDate = useCallback(
    (next: Date) => {
      setDateState(next);
      clearCustomTimes();
    },
    [clearCustomTimes]
  );

  const setTimeSlot = useCallback(
    (next: TimeSlot) => {
      setTimeSlotState(next);
      clearCustomTimes();
    },
    [clearCustomTimes]
  );

  return {
    qualification,
    date,
    timeSlot,
    startTime: customStartTime ?? defaultTimes?.start ?? noDefaultFallbackStartTime,
    endTime: customEndTime ?? defaultTimes?.end ?? noDefaultFallbackEndTime,
    setQualification,
    setDate,
    setTimeSlot,
    setStartTime: setCustomStartTime,
    setEndTime: setCustomEndTime,
    defaultTimes,
    isQualificationSpecificEnabled,
  };
}
